<template>
  <div>
    <div
      v-if="isLoading"
      v-loading="isLoading"
      class="mt-5"
    />

    <template v-else>
      <el-card
        shadow="never"
        class="w-100 mb-3"
      >
        <PeekHeader :pickup="pickup" />
      </el-card>

      <PickupEditForm :pickup="pickup" />
    </template>
  </div>
</template>

<script>
import PeekHeader from '@/components/Application/CustomerService/Pickups/Pickup/Peek/Header'
import PickupEditForm from '@/components/Application/CustomerService/Pickups/Pickup/EditForm'
import { statusAllows } from '@/components/Application/CustomerService/Pickups/Pickup/PickupFunctions'
import PickupCreateDialog from '@/components/Application/CustomerService/Pickups/Pickup/CreateDialog'
import Vue from 'vue'

export default {
  components: {
    PeekHeader,
    PickupEditForm
  },
  data () {
    return {
      breadcrumb: {
        name: 'Details',
        route: { name: 'customerservice-pickups-pickup-details', params: { pickupId: this.$route.params.pickupId } }
      },
      isLoading: true,
      pickup: null,
      pickupCreateDialogVisible: false,
      pickupCreateDialogInstance: undefined
    }
  },
  computed: {
    title () {
      return this.pickup ? `${this.pickup.booking_reference}` : 'Pickup'
    },
    actions () {
      const actions = []

      actions.push([
        {
          name: 'Duplicate',
          icon: ['fal', 'copy'],
          event: this.duplicatePickup
        }
      ])

      if (this.pickup instanceof this.$sdk.pickup) {
        actions.push([
          {
            name: 'Cancel',
            icon: ['fal', 'ban'],
            hidden: !this.canCancelPickup,
            event: this.promptPickupCancellationReason
          },
          {
            name: 'Hold',
            icon: ['fal', 'hand-paper'],
            hidden: !this.canHoldPickup,
            event: this.holdPickup
          },
          {
            name: 'Process',
            icon: ['fal', 'paper-plane'],
            hidden: !this.canProcessPickup,
            event: this.processPickup
          },
          {
            name: 'Push',
            icon: ['fal', 'share'],
            hidden: !this.canPushPickup,
            event: this.alertPickupPushNotice
          },
          {
            name: 'Confirm',
            icon: ['fal', 'check-double'],
            hidden: !this.canConfirmPickup,
            event: this.promptPickupConfirmationReference
          }
        ])

        actions.push([
          {
            name: 'Re-Book',
            icon: ['fal', 'repeat'],
            event: this.reBookPickup
          }
        ])
      }

      return actions
    },
    canCancelPickup () {
      return statusAllows(this.pickup.status, 'cancelled')
    },
    canHoldPickup () {
      return statusAllows(this.pickup.status, 'held')
    },
    canProcessPickup () {
      return statusAllows(this.pickup.status, 'pending')
    },
    canPushPickup () {
      return this.canProcessPickup
    },
    canConfirmPickup () {
      return statusAllows(this.pickup.status, 'confirmed')
    }
  },
  watch: {
    pickupCreateDialogVisible (visible) {
      if (!visible && typeof this.pickupCreateDialogInstance !== 'undefined') {
        this.pickupCreateDialogInstance.$destroy()
        this.pickupCreateDialogInstance.$el.remove()
        this.pickupCreateDialogInstance = undefined
      }
    }
  },
  created () {
    this
      .$sdk
      .pickup
      .read(this.$route.params.pickupId, request => {
        request.include = [
          'account.carrier_settings.carrier',
          'account.carrier_service_settings.carrier_service.carrier',
          'account.teams.shipper_setting.address',
          'profile',
          'carrier',
          'carrier_service',
          'sender',
          'receiver',
          'consignments.items.type',
          'items.type',
          'customer_time_zone',
          'team.shipper_setting.address',
          'events.profile',
          'notes.creator',
          'consignments.trackings'
        ]
      })
      .then(res => { this.pickup = res })
      .finally(() => { this.isLoading = false })
  },
  methods: {
    promptPickupCancellationReason () {
      this
        .$prompt(
          'Enter a Cancellation Reason',
          `Cancel Pickup ${this.pickup.booking_reference}`,
          {
            inputValidator: value => {
              return value && value.length > 0 ? true : 'A cancellation reason is required'
            },
            confirmButtonText: 'Confirm',
            cancelButtonText: 'Cancel',
            callback: (action, instance) => {
              if (action === 'confirm') {
                this.cancelPickup(`Cancellation reason: ${instance.inputValue}`)
              }
            }
          }
        )
    },
    promptPickupConfirmationReference () {
      this
        .$prompt(
          'Enter a Confirmation Reference',
          `Confirm Pickup ${this.pickup.booking_reference}`,
          {
            inputValidator: value => {
              return value && value.length > 0 ? true : 'A confirmation reference is required'
            },
            confirmButtonText: 'Confirm',
            cancelButtonText: 'Cancel',
            callback: (action, instance) => {
              if (action === 'confirm') {
                this.confirmPickup(instance.inputValue)
              }
            }
          }
        )
    },
    alertPickupPushNotice () {
      this
        .$alert(
          `Pushing a Pickup will skip OpenFreight validation rules but will not force ${this.pickup.carrier.name} to accept the Pickup request if the Pickup information is invalid.`,
          'Notice',
          {
            callback: action => {
              if (action === 'confirm') {
                this.pushPickup()
              }
            }
          }
        )
    },
    async cancelPickup (reason) {
      this.isLoading = true

      await this
        .$sdk
        .note
        .create({
          content: reason,
          resource: 'pickup',
          resource_uuid: this.pickup.id
        })

      try {
        this.pickup = await this
          .pickup
          .update(
            request => {
              request.body = { status: 'cancelled' }
            }
          )

        this.$message.success(`Updated Pickup ${this.pickup.booking_reference}`)
      } finally {
        this.isLoading = false
      }
    },
    async holdPickup () {
      this.isLoading = true

      try {
        this.pickup = await this
          .pickup
          .update(
            request => {
              const pickup = this.pickup.clone()
              pickup.status = 'held'
              request.body = pickup
            }
          )

        this.$message.success(`Updated Pickup ${this.pickup.booking_reference}`)
      } finally {
        this.isLoading = false
      }
    },
    async processPickup () {
      this.isLoading = true

      try {
        this.pickup = await this
          .pickup
          .update(
            request => {
              const pickup = this.pickup.clone()
              pickup.status = 'pending'
              request.body = pickup
            }
          )

        this.$message.success(`Updated Pickup ${this.pickup.booking_reference}`)
      } finally {
        this.isLoading = false
      }
    },
    async pushPickup () {
      this.isLoading = true

      try {
        this.pickup = await this
          .pickup
          .update(
            request => {
              const pickup = this.pickup.clone()
              pickup.is_forced = true
              pickup.status = 'pending'
              request.body = pickup
            }
          )

        this.$message.success(`Updated Pickup ${this.pickup.booking_reference}`)
      } finally {
        this.isLoading = false
      }
    },
    async confirmPickup (reference) {
      this.isLoading = true

      try {
        this.pickup = await this
          .pickup
          .update(
            request => {
              request.body = { carrier_reference: reference, status: 'confirmed' }
            }
          )

        this.$message.success(`Updated Pickup ${this.pickup.booking_reference}`)
      } finally {
        this.isLoading = false
      }
    },
    async reBookPickup () {
      const pickup = this.pickup.clone()

      await pickup.refresh(request => {
        request.include = [
          'account.carrier_settings.carrier',
          'account.carrier_service_settings.carrier_service.carrier',
          'account.teams.shipper_setting.address',
          'profile',
          'carrier',
          'carrier_service',
          'sender',
          'receiver',
          'consignments.items.type',
          'items.type',
          'customer_time_zone',
          'team.shipper_setting.address',
          'events.profile'
        ]
      })

      const reBookedPickup = pickup.replicate()

      reBookedPickup.id = undefined
      reBookedPickup.booking_reference = undefined
      reBookedPickup.carrier_reference = undefined
      reBookedPickup.customer_pickup_date = new Date()
      reBookedPickup.profile = undefined
      reBookedPickup.status = undefined
      reBookedPickup.previous_pickup = pickup
      reBookedPickup.receiver.id = undefined
      reBookedPickup.sender.id = undefined

      reBookedPickup
        .items
        .map(item => {
          item.id = undefined
          item.created_at = undefined
          item.updated_at = undefined
        })

      reBookedPickup
        .notes
        .map(note => {
          note.id = undefined
        })

      this.createPickup(reBookedPickup, pickup)
    },
    createPickup (pickup, previous_pickup = null) {
      const PickupCreateDialogClass = Vue.extend(PickupCreateDialog)

      this.pickupCreateDialogInstance = new PickupCreateDialogClass({
        propsData: {
          pickup,
          previousPickup: previous_pickup
        }
      })

      this
        .pickupCreateDialogInstance
        .$on('destroy', () => {
          this.pickupCreateDialogVisible = false
        })

      this
        .pickupCreateDialogInstance
        .$mount()

      this
        .$root
        .$el
        .appendChild(this.pickupCreateDialogInstance.$el)

      this.pickupCreateDialogVisible = true
    },
    async duplicatePickup () {
      const pickup = this.pickup.clone()

      await pickup.refresh(request => {
        request.include = [
          'account.carrier_settings.carrier',
          'account.carrier_service_settings.carrier_service.carrier',
          'account.teams.shipper_setting.address',
          'profile',
          'carrier',
          'carrier_service',
          'sender',
          'receiver',
          'customer_time_zone',
          'team.shipper_setting.address',
          'events.profile'
        ]
      })

      const duplicatePickup = pickup.replicate()

      duplicatePickup.id = undefined
      duplicatePickup.booking_reference = undefined
      duplicatePickup.carrier_reference = undefined
      duplicatePickup.customer_pickup_date = new Date()
      duplicatePickup.profile = undefined
      duplicatePickup.status = undefined
      duplicatePickup.previous_pickup = undefined
      duplicatePickup.consignments = undefined
      duplicatePickup.items = undefined
      duplicatePickup.customer_reference = undefined
      duplicatePickup.collection_name = undefined
      duplicatePickup.special_instructions = undefined
      duplicatePickup.cost_centre = undefined
      duplicatePickup.carrier_reference = undefined
      duplicatePickup.notes = undefined
      duplicatePickup.receiver.id = undefined
      duplicatePickup.sender.id = undefined
      duplicatePickup.events = undefined

      this.createPickup(duplicatePickup)
    }
  }
}
</script>
